import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, BookOpen, Brush, ArrowLeft } from "lucide-react";
import StudioShell from "../components/studio/StudioShell";
import WarliActivity from "../components/studio/WarliActivity";
import { useStamp, StampToast } from "../components/studio/Passport";
import { WARLI_FACTS, WARLI_FIGURES } from "../data/warli";

const STEPS = [
  { id: "learn", label: "1. Learn", icon: BookOpen },
  { id: "build", label: "2. Build", icon: Brush },
];

export default function WarliStudio() {
  const [step, setStep] = useState("learn");
  const { earn, showToast, dismiss } = useStamp("warli");

  return (
    <StudioShell>
      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-8 sm:pt-16 sm:pb-12">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg viewBox="0 0 1200 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
            {Array.from({ length: 6 }).map((_, i) => (
              <g key={i} transform={`translate(${i * 220 + 100}, 200) scale(0.7)`} color="#7B3F1D">
                {WARLI_FIGURES[i % WARLI_FIGURES.length].svg}
              </g>
            ))}
          </svg>
        </div>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="inline-flex items-center gap-2 text-[#7B3F1D]">
            <Sparkles size={18} />
            <span className="overline !text-[#7B3F1D]">Module 2 · Maharashtra</span>
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
            Paint a Warli
            <br />
            <em className="italic font-light">village wall.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
            Place dancers, drummers, huts, animals, and trees onto a mud wall — and watch your
            own little village come alive in white rice-flour figures.
          </p>
        </div>
      </section>

      {/* Step picker */}
      <nav className="max-w-6xl mx-auto px-4 sm:px-10" data-testid="warli-steps">
        <div className="grid grid-cols-2 gap-2 sm:gap-4 p-2 sm:p-3 rounded-full bg-white border border-[#E5DFD3] shadow-sm">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const active = step === s.id;
            return (
              <button
                key={s.id}
                data-testid={`warli-step-${s.id}`}
                onClick={() => setStep(s.id)}
                className={`flex items-center justify-center gap-2 px-3 sm:px-5 py-3 rounded-full text-xs sm:text-sm tracking-wide transition-all duration-500 ${
                  active ? "bg-[#7B3F1D] text-[#F9F6F0]" : "text-[#5C6A8F] hover:text-[#1E2A4F]"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.label.split(". ")[1]}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10 sm:py-16 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
          >
            {step === "learn" && <WarliLearn />}
            {step === "build" && (
              <>
                <div className="text-center mb-10">
                  <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
                    Build your Warli village.
                  </h2>
                  <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
                    Tap a figure to add it to the wall. Drag to move. Double-tap to remove. Save
                    when your village feels just right.
                  </p>
                </div>
                <WarliActivity onSave={earn} />
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          {step === "build" && (
            <button
              data-testid="warli-prev"
              onClick={() => setStep("learn")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
            >
              <ArrowLeft size={14} /> Previous
            </button>
          )}
          {step === "learn" && (
            <button
              data-testid="warli-next"
              onClick={() => setStep("build")}
              className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#7B3F1D] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
            >
              Start building →
            </button>
          )}
        </div>
      </section>

      <StampToast show={showToast} artName="Warli" onDismiss={dismiss} />
    </StudioShell>
  );
}

function WarliLearn() {
  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
          The story of Warli.
        </h2>
        <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
          A 2,500-year-old tribal art form told entirely in circles, triangles, and lines.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-10">
        {WARLI_FACTS.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`warli-fact-${i}`}
            className="p-6 sm:p-7 rounded-2xl bg-white border border-[#E5DFD3] hover:border-[#7B3F1D] transition-colors duration-500"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F1E4D3] text-2xl">
              {f.emoji}
            </span>
            <h3 className="font-display text-2xl text-[#1E2A4F] mt-4">{f.title}</h3>
            <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-2">{f.body}</p>
          </motion.div>
        ))}
      </div>

      {/* Sample showcase */}
      <div className="p-8 sm:p-12 rounded-2xl bg-[#7B3F1D]" data-testid="warli-sample">
        <p className="overline !text-[#F1E4D3] text-center">A glimpse of the art</p>
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-8 gap-4">
          {WARLI_FIGURES.map((fig) => (
            <div key={fig.id} className="flex flex-col items-center">
              <svg viewBox="0 0 100 100" className="w-full max-w-[80px] aspect-square" color="#F9F6F0">
                {fig.svg}
              </svg>
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#F1E4D3]/70 mt-2 text-center">
                {fig.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#1E2A4F] text-[#F9F6F0] text-center">
        <p className="overline !text-[#E76F51]">A note from Meera</p>
        <p className="font-display italic text-xl sm:text-2xl mt-3 leading-snug max-w-2xl mx-auto">
          &ldquo;In Warli, every figure is made from the same simple shapes you already know. If you
          can draw a triangle, you can paint a village.&rdquo;
        </p>
      </div>
    </div>
  );
}
